import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, onBeforeRouteLeave } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useDebounceFn } from '@vueuse/core'
import { DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce'

import { useCalculationForm } from '@/components/calculation-form/composable/useCalculationForm'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'
import { useBuildingBlockStore } from '@/stores/buildingBlock'
import { useQuantityBuildingBlockStore } from '@/stores/quantityBuildingBlock'
import type { QuantityBuildingBlock } from '@/stores/type/quantityBuildingBlock.type'
import { useCalculationStore } from '@/stores/calculation'
import { useConfirmDeleteStore } from '@/stores/confirmDelete'
import type { DesignLinkImportNotice } from '@/components/building-block-form/utils/switchValidation'
import { ISSUE_SEVERITIES } from '@/constants/issueSeverity'
import { normalizeLabels } from '@/views/calculations/utils/quantityLabels'
import {
  buildDesignLinkChangedMessages,
  buildDesignLinkImportChangedIssueMap,
  buildDesignLinkMissingMessages,
  buildSelectedBlockIdSet,
  resolveDesignLinkImportIssueChipColor,
  resolveDesignLinkImportIssueClass,
  resolveDesignLinkUnitMismatch,
  resolveBuildingBlockUnit,
  resolveDesignLinkNoticeTitle,
  type DesignLinkImportIssueByQuantityId,
  type DesignLinkUnitMismatchByQuantityId
} from '@/components/calculation-form/design-link/utils/designLinksUtils'

type BlockUnitById = Record<number, string>
type PendingQuantityAlias = { quantity: QuantityBuildingBlock; alias: string }

export function useDesignLinks(props: CalculationFormMixinProps) {
  const { isValid, isReadOnly } = useCalculationForm(props)

  const route = useRoute()
  const calculationId = computed(() => Number(route.params.calculationId))

  const quantityBuildingBlockStore = useQuantityBuildingBlockStore()
  const buildingBlockStore = useBuildingBlockStore()
  const calculationStore = useCalculationStore()
  const confirmDeleteStore = useConfirmDeleteStore()

  const { items: quantityBuildingBlockItems } = storeToRefs(quantityBuildingBlockStore)
  const { items: buildingBlockItems } = storeToRefs(buildingBlockStore)
  const { designLinkSwitchNotice, designLinkImportNotice } = storeToRefs(calculationStore)

  const selectedBlockIdSet = computed(() =>
    buildSelectedBlockIdSet(buildingBlockItems.value, quantityBuildingBlockItems.value)
  )

  const fileInput = ref<HTMLInputElement | null>(null)
  const labelMenuQuantityId = ref<number | null>(null)
  const labelDraft = ref<string[]>([])

  const labelSuggestions = computed(() =>
    normalizeLabels(
      quantityBuildingBlockItems.value.flatMap((quantity) => quantity.labels ?? []),
      Number.POSITIVE_INFINITY
    )
  )

  const clearStaleBlockReferences = async () => {
    const existingBlockIds = new Set<number>()
    const staleQuantities: QuantityBuildingBlock[] = []

    for (const buildingBlock of buildingBlockItems.value) {
      existingBlockIds.add(Number(buildingBlock.id))
    }

    for (const quantity of quantityBuildingBlockItems.value) {
      if (
        typeof quantity.building_block === 'number' &&
        !existingBlockIds.has(Number(quantity.building_block))
      ) {
        staleQuantities.push(quantity)
      }
    }

    if (staleQuantities.length === 0) return

    await Promise.all(
      staleQuantities.map((quantity) =>
        quantityBuildingBlockStore.update({
          ...quantity,
          building_block: null,
          unit_mismatch_accepted_key: null
        })
      )
    )
  }

  const previousBlockUnitMap = ref<BlockUnitById>({})

  const buildBlockUnitMap = (blocks: typeof buildingBlockItems.value) => {
    const map: BlockUnitById = {}
    for (const block of blocks) {
      map[block.id] = block.unit ?? ''
    }
    return map
  }

  watch(
    buildingBlockItems,
    async (newBlocks) => {
      const prevMap = previousBlockUnitMap.value
      if (Object.keys(prevMap).length === 0) {
        previousBlockUnitMap.value = buildBlockUnitMap(newBlocks)
        await clearStaleBlockReferences()
        return
      }

      const quantitiesToClear: QuantityBuildingBlock[] = []
      for (const quantity of quantityBuildingBlockItems.value) {
        if (typeof quantity.building_block !== 'number') continue
        if (!quantity.unit_mismatch_accepted_key) continue

        const newBlock = newBlocks.find((b) => b.id === quantity.building_block)
        if (!newBlock) continue

        const prevUnit = prevMap[quantity.building_block] ?? ''
        const newUnit = newBlock.unit ?? ''

        if (prevUnit !== newUnit) {
          quantitiesToClear.push(quantity)
        }
      }

      if (quantitiesToClear.length > 0) {
        await Promise.all(
          quantitiesToClear.map((quantity) =>
            quantityBuildingBlockStore.update({
              ...quantity,
              unit_mismatch_accepted_key: null
            })
          )
        )
      }

      previousBlockUnitMap.value = buildBlockUnitMap(newBlocks)
      await clearStaleBlockReferences()
    },
    { deep: true }
  )

  onMounted(async () => {
    await quantityBuildingBlockStore.fetchItems({ calculation: calculationId.value })
    await buildingBlockStore.fetchItems({ calculation: calculationId.value })
    previousBlockUnitMap.value = buildBlockUnitMap(buildingBlockItems.value)
    await clearStaleBlockReferences()
  })

  const buildingBlockUnit = computed(
    () => (buildingBlockId: number | null | undefined) =>
      resolveBuildingBlockUnit(buildingBlockItems.value, buildingBlockId)
  )

  const designLinkMissingMessages = computed(() =>
    buildDesignLinkMissingMessages(designLinkSwitchNotice.value)
  )
  const designLinkChangedMessages = computed(() =>
    buildDesignLinkChangedMessages(designLinkSwitchNotice.value)
  )
  const designLinkNoticeTitle = computed(() =>
    resolveDesignLinkNoticeTitle(designLinkSwitchNotice.value)
  )

  const importMissingIssueByQuantityId = computed<DesignLinkImportIssueByQuantityId>(() => {
    const issues: DesignLinkImportIssueByQuantityId = {}

    for (const quantity of quantityBuildingBlockItems.value) {
      if (!quantity.is_missing_from_last_import) continue

      issues[Number(quantity.id)] = {
        severity: ISSUE_SEVERITIES.MISSING,
        rowName: quantity.name,
        unit: quantity.unit,
        title: 'Missing row in uploaded quantity file',
        summary: `Quantity row ${quantity.name} is missing in the uploaded file.`,
        fieldDiffs: []
      }
    }

    return issues
  })
  const importChangedIssueByQuantityId = computed(() =>
    buildDesignLinkImportChangedIssueMap(designLinkImportNotice.value)
  )

  const unitMismatchByQuantityId = computed<DesignLinkUnitMismatchByQuantityId>(() => {
    const mismatches: DesignLinkUnitMismatchByQuantityId = {}

    for (const quantity of quantityBuildingBlockItems.value) {
      const mismatch = resolveDesignLinkUnitMismatch(quantity, buildingBlockItems.value)
      if (mismatch) {
        mismatches[quantity.id] = mismatch
      }
    }

    return mismatches
  })

  const hasCurrentUnitMismatch = (quantity: QuantityBuildingBlock) => {
    return quantity.id in unitMismatchByQuantityId.value
  }

  const hasMissingBuildingBlockUnit = (quantity: QuantityBuildingBlock) => {
    if (typeof quantity.building_block !== 'number') return false
    const unit = resolveBuildingBlockUnit(buildingBlockItems.value, quantity.building_block)
    return !unit || unit.trim() === ''
  }

  const isUnitMismatchAccepted = (quantity: QuantityBuildingBlock) => {
    const mismatch = unitMismatchByQuantityId.value[quantity.id]
    return mismatch ? quantity.unit_mismatch_accepted_key === mismatch.key : false
  }

  const hasUnacceptedUnitMismatch = (quantity: QuantityBuildingBlock) => {
    return hasCurrentUnitMismatch(quantity) && !isUnitMismatchAccepted(quantity)
  }

  const hasBlockingUnitMismatches = computed(() => {
    return quantityBuildingBlockItems.value.some((quantity) => hasUnacceptedUnitMismatch(quantity))
  })
  const hasMissingImportIssues = computed(
    () => Object.keys(importMissingIssueByQuantityId.value).length > 0
  )

  const getImportIssueForQuantity = (quantity: QuantityBuildingBlock) => {
    return (
      importMissingIssueByQuantityId.value[Number(quantity.id)] ??
      importChangedIssueByQuantityId.value[Number(quantity.id)] ??
      null
    )
  }

  watch(
    [quantityBuildingBlockItems, hasMissingImportIssues],
    ([quantityItems, hasMissingIssue]) => {
      isValid.value =
        quantityItems.length > 0 &&
        quantityItems.some((quantity) => quantity.building_block != null) &&
        !hasBlockingUnitMismatches.value &&
        !hasMissingIssue
    },
    { deep: true, immediate: true }
  )

  const handleImportFile = () => {
    if (fileInput.value) fileInput.value.value = ''
    fileInput.value?.click()
  }

  const isImporting = ref(false)
  const importedRowCount = ref<number | null>(null)
  const showImportSummary = ref(false)

  const onFileSelected = async (event: Event) => {
    if (!(event.target instanceof HTMLInputElement)) return
    const target = event.target
    const file = target.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)
    formData.append('calculation', calculationId.value.toString())

    isImporting.value = true
    try {
      const response = await quantityBuildingBlockStore.updateFile(formData)
      importedRowCount.value = response?.total_rows ?? 0
      showImportSummary.value = true
      const missingRows = response?.design_link_missing_rows ?? []
      const changedRows = response?.design_link_changed_rows ?? []

      if (missingRows.length || changedRows.length) {
        const importNotice = {
          summary: response?.design_link_validation_summary,
          missingRows,
          changedRows
        } as DesignLinkImportNotice
        calculationStore.setDesignLinkImportNotice(importNotice)
        calculationStore.setSnackbarMessage(
          'Some Design Links were affected by the imported quantity file. Please review the Design Links tab.',
          'warning'
        )
      } else {
        calculationStore.setDesignLinkImportNotice(null)
      }
    } catch (error) {
      console.error('Failed to import quantities:', error)
    } finally {
      isImporting.value = false
    }
    await quantityBuildingBlockStore.fetchItems({ calculation: calculationId.value })
  }

  const updateBuildingBlock = async (
    quantity: QuantityBuildingBlock,
    buildingBlockId: number | null
  ) => {
    const updated = {
      ...quantity,
      building_block: buildingBlockId,
      unit_mismatch_accepted_key: null
    }
    await quantityBuildingBlockStore.update(updated)
    if (buildingBlockId != null) {
      calculationStore.clearDesignLinkImportNoticeForQuantityRow(Number(quantity.id))
    }
  }

  const confirmQuantityRow = async (quantity: QuantityBuildingBlock) => {
    if (isReadOnly.value) return
    const mismatch = unitMismatchByQuantityId.value[quantity.id]
    if (!mismatch) return

    const accepting = !isUnitMismatchAccepted(quantity)
    await quantityBuildingBlockStore.update({
      ...quantity,
      unit_mismatch_accepted_key: accepting ? mismatch.key : null
    })
    if (accepting) {
      calculationStore.clearDesignLinkImportNoticeForQuantityRow(Number(quantity.id))
    }
  }

  const deleteQuantityRow = async (quantity: QuantityBuildingBlock) => {
    if (isReadOnly.value) return
    const confirmed = await confirmDeleteStore.confirm({
      name: quantity.name ?? 'this quantity row',
      message: 'Delete this quantity row from the design links?'
    })
    if (!confirmed) return

    await quantityBuildingBlockStore.delete(quantity)
  }

  const openLabelMenu = (quantity: QuantityBuildingBlock) => {
    if (isReadOnly.value) return
    if (Number(quantity.id) in importMissingIssueByQuantityId.value) return
    labelMenuQuantityId.value = quantity.id
    labelDraft.value = normalizeLabels(quantity.labels ?? [])
  }

  const closeLabelMenu = () => {
    labelMenuQuantityId.value = null
    labelDraft.value = []
  }

  const setLabelDraft = (value: unknown) => {
    labelDraft.value = normalizeLabels(value)
  }

  const saveQuantityLabels = async (quantity: QuantityBuildingBlock) => {
    if (isReadOnly.value) return
    await quantityBuildingBlockStore.update({
      ...quantity,
      labels: normalizeLabels(labelDraft.value)
    })
    closeLabelMenu()
  }

  const removeQuantityLabel = async (quantity: QuantityBuildingBlock, label: string) => {
    if (isReadOnly.value) return
    if (Number(quantity.id) in importMissingIssueByQuantityId.value) return
    await quantityBuildingBlockStore.update({
      ...quantity,
      labels: normalizeLabels((quantity.labels ?? []).filter((item) => item !== label))
    })
  }

  // localAlias tracks the typed value per row so the input display stays stable
  // while the debounced API call is still pending.
  const localAlias = ref<Record<number, string>>({})

  // Plain (non-reactive) object so the debounce closure always sees the latest value.
  let pendingAliases: Record<number, PendingQuantityAlias> = {}

  const flushPendingAliases = async () => {
    if (Object.keys(pendingAliases).length === 0) return
    const aliasesToSave = Object.values(pendingAliases)
    pendingAliases = {}
    for (const { quantity, alias } of aliasesToSave) {
      try {
        const updated = await quantityBuildingBlockStore.update({
          ...quantity,
          alias: alias || null
        })
        if (updated !== false) continue
        localAlias.value[quantity.id] = quantity.alias ?? ''
        calculationStore.setSnackbarMessage(
          'Unable to save the display name. It may already be used in this calculation.',
          'error'
        )
      } catch (error) {
        localAlias.value[quantity.id] = quantity.alias ?? ''
        const message =
          error instanceof Error
            ? error.message
            : 'Unable to save the display name. It may already be used in this calculation.'
        calculationStore.setSnackbarMessage(message, 'error')
      }
    }
  }

  const debouncedFlushAliases = useDebounceFn(flushPendingAliases, DEFAULT_DEBOUNCE_DELAY)

  onBeforeRouteLeave(async (_to, _from, next) => {
    await flushPendingAliases()
    next()
  })

  const onAliasInput = (quantity: QuantityBuildingBlock, value: string) => {
    if (isReadOnly.value) return
    if (Number(quantity.id) in importMissingIssueByQuantityId.value) return
    localAlias.value[quantity.id] = value
    pendingAliases[quantity.id] = { quantity: { ...quantity }, alias: value }
    debouncedFlushAliases()
  }

  return {
    isReadOnly,
    isImporting,
    importedRowCount,
    showImportSummary,
    quantityBuildingBlockItems,
    buildingBlockItems,
    selectedBlockIdSet,
    fileInput,
    labelMenuQuantityId,
    labelDraft,
    labelSuggestions,
    buildingBlockUnit,
    hasCurrentUnitMismatch,
    hasMissingBuildingBlockUnit,
    hasUnacceptedUnitMismatch,
    hasBlockingUnitMismatches,
    isUnitMismatchAccepted,
    designLinkMissingMessages,
    designLinkChangedMessages,
    designLinkNoticeTitle,
    getImportIssueForQuantity,
    resolveDesignLinkImportIssueClass,
    resolveDesignLinkImportIssueChipColor,
    handleImportFile,
    onFileSelected,
    updateBuildingBlock,
    confirmQuantityRow,
    deleteQuantityRow,
    openLabelMenu,
    closeLabelMenu,
    setLabelDraft,
    saveQuantityLabels,
    removeQuantityLabel,
    localAlias,
    onAliasInput
  }
}
